import { FC } from "react";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";
import { faRobot } from "@fortawesome/pro-solid-svg-icons";
import { faStopwatch } from "@fortawesome/pro-solid-svg-icons";
import {
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  List,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { Chain, PostTaskRequest } from "@protos/ai/v1/tasks";
import { TASKS_PER_BATCH } from "@shared/constants";
import {
  AiTaskQueryParams,
  usePostAiTaskMutation,
  useTimeLookbackRange,
} from "@shared/hooks";
import { FindingsFilters } from "@shared/types";

interface AssistantDialogProps {
  filterOverrides?: FindingsFilters;
  deploymentId: number;
  numFindings: number;
  opened: boolean;
  findingIds: Set<number>;
  waitTime: number;
  runsRemaining: number;
  hasActiveLicense?: boolean;
  onSaveClick?: () => void;
  onClose: () => void;
}

export const AssistantDialog: FC<AssistantDialogProps> = ({
  filterOverrides,
  deploymentId,
  numFindings,
  opened,
  findingIds,
  waitTime,
  runsRemaining,
  hasActiveLicense,
  onSaveClick,
  onClose,
}) => {
  const history = useHistory();
  const [tlr] = useTimeLookbackRange();

  const mutationParamsTask = usePostAiTaskMutation();
  const onClick = () => {
    const search = new URLSearchParams(history.location.search);
    if (filterOverrides) {
      Object.entries(filterOverrides).forEach(([key, value]) => {
        if (value) search.set(key, value);
      });
    }

    // Handle the case where the user selected rule ids and specific findings
    // This is similar to Ignore and CreateTicket
    let queryTask: AiTaskQueryParams | undefined = undefined;
    if (filterOverrides?.rule_id) {
      queryTask = {
        searchParams: search,
        timeLookbackRange: tlr,
      };
    }

    // The request should always include a fixed request json but we can leave it empty if there are no findings to analyze directly
    let fixedTaskRequest: PostTaskRequest = {
      task: {
        deploymentId: deploymentId,
        issueIds: [],
        chains: [],
      },
      queryParams: "",
    };

    if (findingIds.size > 0) {
      fixedTaskRequest = {
        task: {
          deploymentId: deploymentId,
          issueIds: Array.from(findingIds),
          chains: [
            Chain.AUTOTRIAGE_WITH_GUIDANCE_AND_AUTOFIX,
            Chain.ISSUE_TAGS,
          ],
        },
        queryParams: "", // The query params will be set in the query hook
      };
    }
    const promise = mutationParamsTask.mutateAsync({
      deploymentId,
      queryTask: queryTask,
      fixedTask: fixedTaskRequest,
    });

    promise
      .then((numAnalyzed) => {
        // Temp fix for display error when we analyze more findings than queued
        numAnalyzed = Math.min(numAnalyzed, numFindings);
        const time = Math.round(waitTime + numAnalyzed / TASKS_PER_BATCH);
        if (numAnalyzed === numFindings) {
          notifications.show({
            title: "Analysis started",
            icon: <FontAwesomeIcon size="3x" icon={faCheckCircle} />,
            message: (
              <>
                Check back here in approximately {time}{" "}
                {pluralize("minute", time)} to see the results.
              </>
            ),
            autoClose: 5000,
            color: "green",
          });
        } else if (numAnalyzed === 0) {
          notifications.show({
            title: "Unable to analyze your findings",
            icon: <FontAwesomeIcon size="3x" icon={faExclamationCircle} />,
            message: (
              <>
                Semgrep Assistant can only analyze findings from supported SCMs
                that have granted code access. The findings you selected may be
                from local scans, or in repositories not properly connected to
                your SCM.
              </>
            ),
            autoClose: 20000,
            color: "yellow",
          });
        } else {
          notifications.show({
            title: (
              <>
                Unable to analyze {numFindings - numAnalyzed} of {numFindings}
              </>
            ),
            icon: <FontAwesomeIcon size="3x" icon={faExclamationCircle} />,
            message: (
              <>
                Your analysis has started. Semgrep Assistant can only analyze
                findings from supported SCMs that have granted code access.{" "}
                {numFindings - numAnalyzed} of your findings here do not meet
                those requirements. Check back here in approximately {time}{" "}
                {pluralize("minute", time)} to see results for the supported
                findings.
              </>
            ),
            autoClose: 20000,
            color: "yellow",
          });
        }
      })
      .catch((error) => {
        if (error.statusCode === 429) {
          notifications.show({
            title: "Rate Limit Exceeded",
            message: "You have hit the rate limit. Please try again later.",
            withCloseButton: true,
            autoClose: 10000,
            color: "red",
          });
        } else {
          notifications.show({
            title: <>Error</>,
            message: <>Please try again later.</>,
            withCloseButton: true,
            autoClose: 10000,
            color: "red",
          });
        }
      });
    onSaveClick && onSaveClick();
    onClose();
  };
  return (
    <Modal
      centered
      opened={opened}
      size="sm"
      onClose={onClose}
      title={
        <>
          Analyze {numFindings} {pluralize("finding", numFindings)} with
          Assistant
        </>
      }
      styles={{
        body: { padding: 0 },
        title: { paddingLeft: 6 },
      }}
    >
      <Stack>
        <Box mb={10} px={24}>
          <Text fz="sm">Assistant will:</Text>
          <List
            styles={{
              item: {
                wordBreak: "break-word",
              },
            }}
            size="sm"
            withPadding
          >
            <List.Item>
              Recommend either "should fix" or "ignore" for each finding
            </List.Item>
            <List.Item>Construct remediation guidance, if applicable</List.Item>
            <List.Item>Generate code for a fix, if applicable</List.Item>
            <List.Item>
              Tag findings by component (e.g., auth, payments)
            </List.Item>
          </List>
        </Box>
        <Group px={24}>
          <FontAwesomeIcon icon={faStopwatch} size="2x" />
          <div>
            <Text
              fz="xs"
              style={{
                wordWrap: "break-word",
              }}
            >
              Estimated time to run
            </Text>
            <Text
              fz="md"
              style={{
                wordWrap: "break-word",
                fontWeight: "bold",
              }}
            >
              {waitTime} {pluralize("minute", waitTime)}
            </Text>
          </div>
        </Group>
        <Stack justify="space-between" px={24} pb={16}>
          {!hasActiveLicense && (
            <Text
              fz="xs"
              style={{
                flexGrow: 1,
                color: runsRemaining <= 10 ? "orange.9" : "inherit",
              }}
            >
              {runsRemaining} on-demand analysis{" "}
              {pluralize("run", runsRemaining)} remaining this month. Automatic
              triggers for analysis, such as pull request comments, are exempt
              from this limit.
            </Text>
          )}
          <Group justify="end">
            <Button
              size="xs"
              variant="filled"
              onClick={onClick}
              leftSection={<FontAwesomeIcon size="xs" icon={faRobot} />}
            >
              Analyze {numFindings}
            </Button>
          </Group>
        </Stack>

        {runsRemaining <= 10 && !hasActiveLicense && (
          <Alert
            variant="light"
            color="orange"
            style={{
              justifyContent: "space-between",
              padding: "8px 24px 12px 24px",
            }}
          >
            <Text fz="sm" c="orange.8">
              <b>Need more runs?</b> Paid accounts get 1000 Assistant runs per
              month.{" "}
              <Anchor
                href="https://get.semgrep.dev/Book-a-demo.html"
                target="_blank"
                underline="always"
                rel="noopener noreferrer"
                c="orange.8"
                inherit
              >
                Book a demo to learn about all of Assistant's features and our
                pricing.
              </Anchor>
            </Text>
          </Alert>
        )}
      </Stack>
    </Modal>
  );
};
